'use client';

import { createContext, useContext, type ReactNode } from 'react';

/**
 * Host adapter contract for hierarchical category trees — the
 * taxonomy that backs the {@code CategoryTree} block.
 *
 * <p>One source serves every content surface (courses, blog,
 * activities/events, video, …). The {@code type} argument selects
 * which taxonomy to fetch; it maps 1:1 to the trailing path segment of
 * the public CMS endpoint:
 *
 * <pre>{@code
 *   GET /api/service/v1/public/{tenantId}/cms/category/tree/{type}?language={lang}
 * }</pre>
 *
 * <p>e.g. {@code type === 'course'} →
 * {@code .../cms/category/tree/course}. New surfaces (blog, activity)
 * are just new {@code type} values against the same endpoint family —
 * no contract change required.
 *
 * <p>The endpoint returns nested plain JSON (no envelope): each node
 * carries {@code id}, {@code name}, optional {@code imageUrl} /
 * {@code thumbnailUrl} / {@code weight}, and an optional
 * {@code children} array. Hosts normalise that into
 * {@link CategoryTreeNode} below.
 *
 * <p>{@code blog-tags} is a special case: it's not backed by the CMS
 * category tree endpoint but by a flat tag-search endpoint, and its
 * nodes never carry {@code children}. Hosts still return it through
 * {@code getTree} so the same {@code CategoryTree} block can render it.
 *
 * <p>When no provider is mounted, {@link useCategoryTreeSource} returns
 * {@code null}; consuming blocks render an adapter placeholder rather
 * than throwing.
 */

/**
 * A node in a category tree. Mirrors the CMS
 * {@code cms/category/tree/*} payload, normalised. Consumers key off
 * {@code id} (the endpoint provides no slug); {@code slug} is an
 * optional host-supplied convenience for prettier links.
 */
export interface CategoryTreeNode {
  id: number | string;
  name: string;
  /** Optional URL-safe identifier. Falls back to {@code String(id)}
   *  at the block when absent. */
  slug?: string;
  /** Full-size cover image. */
  imageUrl?: string;
  /** Small icon / thumbnail variant. */
  thumbnailUrl?: string;
  /** Per-parent display order (ascending). */
  weight?: number;
  /** Direct descendants. Absent or empty for leaf categories. */
  children?: CategoryTreeNode[];
}

/**
 * Content surface a tree belongs to — the trailing segment of the
 * {@code cms/category/tree/{type}} endpoint. Kept as a widened string
 * (not a closed union) so hosts can add surfaces without a runtime
 * change; the {@code CategoryTree} block's config enumerates the
 * known ones for the admin dropdown.
 */
export type CategoryTreeType =
  | 'course'
  | 'blog'
  | 'blog-category'
  | 'blog-tags'
  | 'activity'
  | 'video'
  | 'micro'
  | 'language'
  | 'channel'
  | 'level'
  | 'registered'
  | 'demanded'
  | (string & {});

export interface CategoryTreeSource {
  /**
   * Hierarchical category tree for the given content surface,
   * server-pre-computed. Returns {@code []} when the host has no
   * categories for that {@code type}.
   *
   * @param type    which taxonomy to fetch (course / blog / activity / …)
   * @param opts    language hint + abort signal
   */
  getTree(
    type: CategoryTreeType,
    opts?: { language?: string; signal?: AbortSignal },
  ): Promise<CategoryTreeNode[]>;
}

/**
 * Pure helper: DFS the tree to find the path root → matching node
 * (by {@code id}). Useful for breadcrumbs / active-ancestor
 * highlighting. Not on {@link CategoryTreeSource} because it's pure
 * traversal — hosts shouldn't re-implement it.
 */
export function findCategoryPath(
  tree: CategoryTreeNode[],
  id: number | string,
): CategoryTreeNode[] {
  for (const node of tree) {
    if (String(node.id) === String(id)) return [node];
    if (node.children && node.children.length > 0) {
      const sub = findCategoryPath(node.children, id);
      if (sub.length > 0) return [node, ...sub];
    }
  }
  return [];
}

const CategoryTreeSourceContext = createContext<CategoryTreeSource | null>(null);

export function CategoryTreeSourceProvider({
  value,
  children,
}: {
  value: CategoryTreeSource;
  children: ReactNode;
}) {
  return (
    <CategoryTreeSourceContext.Provider value={value}>
      {children}
    </CategoryTreeSourceContext.Provider>
  );
}

/** Returns the host-provided source, or {@code null} when no
 *  provider is mounted. */
export function useCategoryTreeSource(): CategoryTreeSource | null {
  return useContext(CategoryTreeSourceContext);
}
